import { motion } from "framer-motion";
import Link from "next/link";
import Image from "next/image";
import { headerVariants } from "../header/HeaderSite";

type ColorVariant = 'blue' | 'green' | 'orange' | 'purple' | 'red' | 'amber' | 'cyan' | 'teal' | 'indigo' | 'pink';

const colorMap: Record<ColorVariant, { blob: string; badge: string }> = {
    blue: {
        blob: "bg-blue-100/50 dark:bg-blue-900/20",
        badge: "bg-blue-50 border-blue-200 text-blue-700 dark:bg-blue-900/30 dark:border-blue-800 dark:text-blue-300"
    },
    green: {
        blob: "bg-green-100/50 dark:bg-green-900/20",
        badge: "bg-green-50 border-green-200 text-green-700 dark:bg-green-900/30 dark:border-green-800 dark:text-green-300"
    },
    orange: {
        blob: "bg-orange-100/50 dark:bg-orange-900/20",
        badge: "bg-orange-50 border-orange-200 text-orange-700 dark:bg-orange-900/30 dark:border-orange-800 dark:text-orange-300"
    },
    purple: {
        blob: "bg-purple-100/50 dark:bg-purple-900/20",
        badge: "bg-purple-50 border-purple-200 text-purple-700 dark:bg-purple-900/30 dark:border-purple-800 dark:text-purple-300"
    },
    red: {
        blob: "bg-red-100/50 dark:bg-red-900/20",
        badge: "bg-red-50 border-red-200 text-red-700 dark:bg-red-900/30 dark:border-red-800 dark:text-red-300"
    },
    amber: {
        blob: "bg-amber-100/50 dark:bg-amber-900/20",
        badge: "bg-amber-50 border-amber-200 text-amber-700 dark:bg-amber-900/30 dark:border-amber-800 dark:text-amber-300"
    },
    cyan: {
        blob: "bg-cyan-100/50 dark:bg-cyan-900/20",
        badge: "bg-cyan-50 border-cyan-200 text-cyan-700 dark:bg-cyan-900/30 dark:border-cyan-800 dark:text-cyan-300"
    },
    teal: {
        blob: "bg-teal-100/50 dark:bg-teal-900/20",
        badge: "bg-teal-50 border-teal-200 text-teal-700 dark:bg-teal-900/30 dark:border-teal-800 dark:text-teal-300"
    },
    indigo: {
        blob: "bg-indigo-100/50 dark:bg-indigo-900/20",
        badge: "bg-indigo-50 border-indigo-200 text-indigo-700 dark:bg-indigo-900/30 dark:border-indigo-800 dark:text-indigo-300"
    },
    pink: {
        blob: "bg-pink-100/50 dark:bg-pink-900/20",
        badge: "bg-pink-50 border-pink-200 text-pink-700 dark:bg-pink-900/30 dark:border-pink-800 dark:text-pink-300"
    },
};

interface LandingHeroProps {
    title: string;
    subtitle: string;
    badge: string;
    heroImage?: string | null;
    ctaText?: string;
    ctaLink?: string;
    color?: ColorVariant;
}

export const LandingHero = ({
    title,
    subtitle,
    badge,
    heroImage,
    ctaText = "Start Chatting Free",
    ctaLink = "/chat",
    color = 'blue'
}: LandingHeroProps) => {
    const styles = colorMap[color] || colorMap.blue;

    return (
        <section className="relative pt-20 pb-32 overflow-hidden">
            <div className={`absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[500px] blur-[120px] rounded-full pointer-events-none ${styles.blob}`} />

            <div className="container mx-auto px-4 relative z-10 text-center">
                <motion.div
                    initial="hidden"
                    animate="visible"
                    variants={headerVariants}
                    className="flex flex-col items-center"
                >
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2 }}
                        className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium mb-8 border ${styles.badge}`}
                    >
                        <span>{badge}</span>
                    </motion.div>

                    <h1 className="text-5xl md:text-7xl font-bold tracking-tight mb-6 bg-clip-text text-transparent bg-gradient-to-b from-gray-900 to-gray-600 dark:from-white dark:to-gray-400 max-w-4xl mx-auto leading-tight pb-2">
                        {title}
                    </h1>

                    <p className="text-xl text-gray-600 dark:text-gray-400 max-w-2xl mx-auto mb-10 leading-relaxed">
                        {subtitle}
                    </p>

                    <div className="flex flex-col sm:flex-row items-center gap-4">
                        <Link
                            href={ctaLink}
                            className="px-8 py-4 bg-gray-900 text-white hover:bg-gray-800 dark:bg-white dark:text-gray-900 dark:hover:bg-gray-100 rounded-lg font-semibold text-lg transition-all shadow-lg hover:shadow-xl"
                        >
                            {ctaText}
                        </Link>
                    </div>

                    {heroImage && (
                        <div className="mt-20 relative w-full max-w-5xl mx-auto rounded-xl border border-gray-200 dark:border-gray-800 shadow-2xl overflow-hidden bg-white/50 dark:bg-gray-900/50 backdrop-blur-sm">
                            <div className="absolute inset-0 bg-gradient-to-t from-white via-transparent to-transparent dark:from-gray-900 dark:via-transparent dark:to-transparent z-10 pointer-events-none" />
                            <Image
                                src={heroImage}
                                alt="Interface Preview"
                                width={1200}
                                height={800}
                                className="w-full h-auto opacity-100 dark:opacity-90"
                                priority
                            />
                            <div className="absolute inset-0 flex items-center justify-center text-gray-400 dark:text-gray-600 font-mono text-sm opacity-20 pointer-events-none">
                                App Interface Preview
                            </div>
                        </div>
                    )}
                </motion.div>
            </div>
        </section>
    );
};
